import { isIdMatch } from '@hero/schema';
import type { MemberType, PartyRole, User } from '@hero/schema';

type UserOrg = User['orgs'][number];

/**
 * Session-scoped driver identity derived from a `User` doc.
 *
 * - **Active org session** (`currentSignedInOrgId` matches an org
 *   row): driver-ness is the in-org `memberType`, not the global
 *   `User.roles[]`. A carrier admin who also drives elsewhere must
 *   not be treated as a driver while signed into the admin Org.
 * - **No active org** (freelancer / individual / continue-as-
 *   individual): fall back to network `User.roles[]`.
 *
 * Pure so splash boot, org-selection (route-param fallback), and
 * `useCurrentUser` all share one definition.
 */
export function resolveDriverSession(user: User | undefined | null): {
  /** Org membership for `currentSignedInOrgId` only — never falls back to `orgs[0]`. */
  activeOrg: UserOrg | undefined;
  /** True when the session is signed into a specific Org. */
  hasActiveOrgSession: boolean;
  /** Active-org `memberType`, or `undefined` when there is no active org session. */
  memberType: MemberType | undefined;
  userRoles: PartyRole[];
  /**
   * True when the *current session* is a driver: `memberType ===
   * 'driver'` in an active org, else `user.roles` includes `driver`.
   */
  isDriver: boolean;
  /** Driver with no active org session (owner-operator / continue-as-individual). */
  isFreelancerDriver: boolean;
  /** Driver membership inside the currently signed-in Org. */
  isCarrierCompanyDriver: boolean;
} {
  const userRoles = (user?.roles ?? []) as PartyRole[];
  const orgs = user?.orgs ?? [];
  const activeOrg =
    user?.currentSignedInOrgId != null
      ? orgs.find(o => isIdMatch(user.currentSignedInOrgId, o._id))
      : undefined;
  const hasActiveOrgSession = !!activeOrg;
  const isDriver = hasActiveOrgSession
    ? activeOrg.memberType === 'driver'
    : userRoles.includes('driver');

  return {
    activeOrg,
    hasActiveOrgSession,
    memberType: activeOrg?.memberType,
    userRoles,
    isDriver,
    isFreelancerDriver: !hasActiveOrgSession && userRoles.includes('driver'),
    isCarrierCompanyDriver: hasActiveOrgSession && activeOrg.memberType === 'driver',
  };
}
